import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';
import { useEffect } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { Navigate } from 'react-router';

import { useTypedParams } from '@sorare/routing';
import {
  Currency,
  UserSourceCategory,
} from '@sorare/core/src/__generated__/globalTypes';
import { GemIcon } from '@sorare/core/src/atoms/icons/GemIcon';
import { MarketCredit } from '@sorare/core/src/atoms/icons/MarketCredit';
import { SorareLogo } from '@sorare/core/src/atoms/icons/SorareLogo';
import { iconCalendar } from '@sorare/core/src/atoms/icons/system/iconCalendar.ts';
import { iconGallery } from '@sorare/core/src/atoms/icons/system/iconGallery.ts';
import { iconJersey_Football } from '@sorare/core/src/atoms/icons/system/iconJersey_Football.ts';
import { iconSearch } from '@sorare/core/src/atoms/icons/system/iconSearch.ts';
import { iconTrophy } from '@sorare/core/src/atoms/icons/system/iconTrophy.ts';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { BrandL } from '@sorare/core/src/atoms/typography';
import { NewSeasonLogo } from '@sorare/core/src/components/Season2027/NewSeasonLogo';
import { ANY_SPORT_HOME } from '@sorare/core/src/constants/__generated__/routes';
import { useSeoContext } from '@sorare/core/src/contexts/seo';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useAmountWithConversion } from '@sorare/core/src/hooks/useAmountWithConversion';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { isType } from '@sorare/core/src/lib/gql';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';
import { css } from '@sorare/css';

import proAffiliateBannerDesktop from '#src/components/invite/SetInviteLanding/assets/pro-affiliate-desktop.webp';
import proAffiliateBannerMobile from '#src/components/invite/SetInviteLanding/assets/pro-affiliate-mobile.webp';
import proBannerDesktop from '#src/components/invite/SetInviteLanding/assets/pro-desktop.webp';
import proBannerMobile from '#src/components/invite/SetInviteLanding/assets/pro-mobile.webp';
import {
  type InviteBullet,
  SetInviteLanding,
} from '#src/components/invite/SetInviteLanding/index.tsx';
import proCardLeft from '#src/components/onboarding/pro/TargetHitLineup/assets/card1.webp';
import proCardCenter from '#src/components/onboarding/pro/TargetHitLineup/assets/card2.webp';
import proCardRight from '#src/components/onboarding/pro/TargetHitLineup/assets/card3.webp';
import { Mode, modeMessages, seriesModeMessages } from '#src/lib/gameMode.ts';

import type {
  SetInviteCreditQuery,
  SetInviteCreditQueryVariables,
  SetInvitePage_cardSet,
  SetInvitePageQuery,
  SetInvitePageQueryVariables,
} from './__generated__/page.graphql.ts';
import type { PageParams } from './__generated__/routeParams.ts';

const SET_INVITE_PAGE_SET_FRAGMENT = gql`
  fragment SetInvitePage_cardSet on CardSet {
    slug
    name
    branding {
      id
      inviteBackgroundDesktop: assetUrl(slot: INVITE_PAGE_BACKGROUND_DESKTOP)
      inviteBackgroundMobile: assetUrl(slot: INVITE_PAGE_BACKGROUND_MOBILE)
      inviteAffiliateBackgroundDesktop: assetUrl(
        slot: INVITE_PAGE_BACKGROUND_AFFILIATE_DESKTOP
      )
      inviteAffiliateBackgroundMobile: assetUrl(
        slot: INVITE_PAGE_BACKGROUND_AFFILIATE_MOBILE
      )
      inviteCards: assetUrl(slot: INVITE_PAGE_CARDS)
      inviteMeta: assetUrl(slot: INVITE_PAGE_META)
      logo: assetUrl(slot: BRANDING_SET_LOGO_COMPOSITE_DESKTOP)
    }
  }
` as TypedDocumentNode<SetInvitePage_cardSet>;

const SET_INVITE_PAGE_QUERY = gql`
  query SetInvitePageQuery($code: String!, $sport: Sport!) {
    fullReferrer(referrerCode: $code) {
      __typename
      ... on User {
        slug
        nickname
        pictureUrl
      }
      ... on UserSource {
        id
        name
        displayName
        pictureUrl
        category
      }
    }
    cardSet {
      id
      currentSet(sport: $sport) {
        slug
        ...SetInvitePage_cardSet
      }
      previousSet(sport: $sport) {
        slug
        ...SetInvitePage_cardSet
      }
    }
  }
  ${SET_INVITE_PAGE_SET_FRAGMENT}
  ${useMonetaryAmount.fragments.monetaryAmount}
` as TypedDocumentNode<SetInvitePageQuery, SetInvitePageQueryVariables>;

/*
 * Fetched separately and uncached: both affiliate variants come back with the
 * same blank id, so writing them to the normalised cache would collapse the two
 * into a single entry. `endDate` is left out as well — it is null despite being
 * non-nullable, which errors the whole field.
 */
const SET_INVITE_CREDIT_QUERY = gql`
  query SetInviteCreditQuery($sport: Sport!, $affiliate: Boolean!) {
    market {
      id
      onboardingConversionCredit(sport: $sport, affiliate: $affiliate) {
        id
        maxDiscount {
          ...useMonetaryAmount_monetaryAmount
        }
      }
    }
  }
  ${useMonetaryAmount.fragments.monetaryAmount}
` as TypedDocumentNode<SetInviteCreditQuery, SetInviteCreditQueryVariables>;

// AppsFlyer OneLink context per invite mode and inviter — see Linear PLT-4889.
const MODE_TO_APPSFLYER: Record<
  string,
  { campaign: string; deepLinkValue: string }
> = {
  arcade: { campaign: 'set_referral', deepLinkValue: 'set_referral' },
  pro: { campaign: 'pro_referral', deepLinkValue: 'pro_referral' },
  squad: {
    campaign: 'squad_invitation',
    deepLinkValue: 'set_squad_invitation',
  },
};

const MODE_FAMILY: Record<string, 'set' | 'pro'> = {
  arcade: 'set',
  squad: 'set',
  pro: 'pro',
};

const AFFILIATE_APPSFLYER: Record<
  'set' | 'pro',
  Record<UserSourceCategory, { pid: string; deepLinkValue: string }>
> = {
  set: {
    [UserSourceCategory.INFLUENCER]: {
      pid: 'set_influencer',
      deepLinkValue: 'set_influencer_referral',
    },
    [UserSourceCategory.CREATOR]: {
      pid: 'set_creator',
      deepLinkValue: 'set_creator_referral',
    },
    [UserSourceCategory.COMMUNITY]: {
      pid: 'set_community',
      deepLinkValue: 'set_community_referral',
    },
    [UserSourceCategory.PARTNERSHIP]: {
      pid: 'set_partnership',
      deepLinkValue: 'set_partnership_referral',
    },
  },
  pro: {
    [UserSourceCategory.INFLUENCER]: {
      pid: 'pro_influencer',
      deepLinkValue: 'pro_influencer_referral',
    },
    [UserSourceCategory.CREATOR]: {
      pid: 'pro_micro_creator',
      deepLinkValue: 'pro_creator_referral',
    },
    [UserSourceCategory.COMMUNITY]: {
      pid: 'pro_community',
      deepLinkValue: 'pro_community_referral',
    },
    [UserSourceCategory.PARTNERSHIP]: {
      pid: 'pro_partnership',
      deepLinkValue: 'pro_partnership_referral',
    },
  },
};

type Affiliate = { category: UserSourceCategory; name: string };

const ONELINK_BASE = 'https://join.sorare.com/WFGF';

const getAppsFlyerContext = (mode: string, affiliate: Affiliate | null) => {
  if (affiliate) {
    const family = MODE_FAMILY[mode] ?? 'set';
    const { pid, deepLinkValue } =
      AFFILIATE_APPSFLYER[family][affiliate.category];
    return { pid, campaign: affiliate.name, deepLinkValue };
  }
  const { campaign, deepLinkValue } =
    MODE_TO_APPSFLYER[mode] ?? MODE_TO_APPSFLYER.arcade;
  return { pid: 'social', campaign, deepLinkValue };
};

export const buildJoinHref = (
  mode: string,
  code: string,
  sport: string,
  affiliate: Affiliate | null
) => {
  const { pid, campaign, deepLinkValue } = getAppsFlyerContext(mode, affiliate);
  const params = new URLSearchParams({
    pid,
    c: campaign,
    deep_link_value: deepLinkValue,
    deep_link_sub1: `signup_code:${code}`,
    deep_link_sub3: `sport:${sport}`,
    // OneLink forwards its query string to the desktop fallback URL, where
    // `persistsQueryStringParameters` stores it as the sign-up referrer code.
    signup_code: code,
  });
  return `${ONELINK_BASE}?${params.toString()}`;
};

/*
 * Gem amounts are hardcoded because the backend only exposes them to signed-in
 * users (`CardSetRoot.inviteTaskAsRefereeRewardConfigs` is nil for visitors) and
 * never exposes the boosted affiliate amount at all. This landing page is always
 * visited logged out. Keep in sync with the referee reward task configs.
 */
const GEMS_REWARD = { regular: 25, affiliate: 50 };

const seoMessages = defineMessages({
  title: { defaultMessage: '{name} invites you to play Sorare {mode}' },
  description: {
    defaultMessage:
      'Join {name} on Sorare {mode}, collect cards and claim your welcome reward.',
  },
});

type ModeConfig = {
  labelMessage: MessageDescriptor;
  accent: string;
  fallbackColor: string;
  ctaLabel: ReactNode;
  bullets: InviteBullet[];
  rewardKind: 'gems' | 'credit';
  /*
   * Artwork shipped with the app, for Pro — a season, not a card set, so it has
   * no branding to read. `arcade` and `squad` leave these out and take their
   * banner and card art from the current set's `INVITE_PAGE_*` branding slots,
   * uploaded per set in the admin.
   */
  cardImageUrls?: string[];
  banner?: BannerImages;
  affiliateBanner?: BannerImages;
};

type BannerImages = { mobile: string; desktop: string };

const PRO_BANNER = { mobile: proBannerMobile, desktop: proBannerDesktop };
const PRO_AFFILIATE_BANNER = {
  mobile: proAffiliateBannerMobile,
  desktop: proAffiliateBannerDesktop,
};
/*
 * Placeholder Pro cards reused from the Pro onboarding: same Early Access
 * artwork family as the design, but different clubs. Swap for the Figma exports.
 */
const PRO_CARDS = [proCardLeft, proCardCenter, proCardRight];

const MODE_CONFIG: Record<string, ModeConfig> = {
  arcade: {
    labelMessage: seriesModeMessages.arcade,
    accent: '#33e0d7',
    fallbackColor: '#1b3a8f',
    rewardKind: 'gems',
    bullets: [
      {
        key: 'collect',
        icon: iconGallery,
        label: <FormattedMessage defaultMessage="Collect cool cards" />,
      },
      {
        key: 'play',
        icon: iconJersey_Football,
        label: (
          <FormattedMessage defaultMessage="Play with your favorite players" />
        ),
      },
      {
        key: 'win',
        icon: iconTrophy,
        label: <FormattedMessage defaultMessage="Win exclusive rewards" />,
      },
    ],
    ctaLabel: <FormattedMessage defaultMessage="Start playing Arcade" />,
  },
  pro: {
    labelMessage: modeMessages[Mode.PRO],
    accent: 'var(--c-inventory-orange)',
    fallbackColor: '#3a1010',
    rewardKind: 'credit',
    cardImageUrls: PRO_CARDS,
    banner: PRO_BANNER,
    affiliateBanner: PRO_AFFILIATE_BANNER,
    bullets: [
      {
        key: 'scout',
        icon: iconSearch,
        label: (
          <FormattedMessage defaultMessage="Scout, Buy, Trade & Sell players you want" />
        ),
      },
      {
        key: 'win',
        icon: iconTrophy,
        label: (
          <FormattedMessage defaultMessage="Win Cards & Cash rewards up to 200K€" />
        ),
      },
      {
        key: 'season',
        icon: iconCalendar,
        label: <FormattedMessage defaultMessage="Play all year long" />,
      },
    ],
    ctaLabel: <FormattedMessage defaultMessage="Start playing Pro" />,
  },
  // TODO: confirm Squad copy/reward once the Figma screen is reviewed; reusing
  // the Arcade gems layout for now.
  squad: {
    labelMessage: seriesModeMessages.squad,
    accent: '#33e0d7',
    fallbackColor: '#4b1e8f',
    rewardKind: 'gems',
    bullets: [
      {
        key: 'collect',
        icon: iconGallery,
        label: <FormattedMessage defaultMessage="Collect cool cards" />,
      },
      {
        key: 'play',
        icon: iconJersey_Football,
        label: (
          <FormattedMessage defaultMessage="Play with your favorite players" />
        ),
      },
      {
        key: 'win',
        icon: iconTrophy,
        label: <FormattedMessage defaultMessage="Win exclusive rewards" />,
      },
    ],
    ctaLabel: <FormattedMessage defaultMessage="Start playing Squad" />,
  },
};

const root = css`
  min-height: var(--100vh);
`;

const setLogo = css`
  width: 280px;
  max-width: 100%;
`;

const SetInvitePage = () => {
  const { mode, slug } = useTypedParams<PageParams>();
  const { sport } = useSportContext();
  const { formatMessage } = useIntl();
  const { setPageMetadata } = useSeoContext();
  const { toMonetaryAmount } = useMonetaryAmount();
  const modeConfig = MODE_CONFIG[mode] ?? MODE_CONFIG.arcade;

  const { data, loading } = useQuery(SET_INVITE_PAGE_QUERY, {
    variables: { code: slug, sport },
  });

  const referrer = data?.fullReferrer;
  const isAffiliate = !!referrer && isType(referrer, 'UserSource');
  const inviterName = (() => {
    if (!referrer) return '';
    if (isType(referrer, 'User')) return referrer.nickname;
    return referrer.displayName || referrer.name;
  })();
  const affiliate =
    referrer && isType(referrer, 'UserSource') && referrer.category
      ? { category: referrer.category, name: referrer.name }
      : null;

  const gemsAmount = isAffiliate ? GEMS_REWARD.affiliate : GEMS_REWARD.regular;
  const { data: creditData } = useQuery(SET_INVITE_CREDIT_QUERY, {
    variables: { sport, affiliate: isAffiliate },
    skip: !referrer || modeConfig.rewardKind !== 'credit',
    fetchPolicy: 'no-cache',
  });
  const conversionCredit = creditData?.market?.onboardingConversionCredit;
  const { main: creditAmount } = useAmountWithConversion({
    monetaryAmount: toMonetaryAmount(conversionCredit?.maxDiscount),
    primaryCurrency: Currency.FIAT,
    hideFractionDigits: true,
  });

  const modeLabel = formatMessage(modeConfig.labelMessage);
  /*
   * `currentSet` is null while a set transition is in flight. Fall back to the
   * set that just ended so a live invite link keeps its art and its name
   * instead of going blank. The fallback picks a whole set, never an individual
   * slot: an empty slot on the live set stays empty.
   */
  const set = data?.cardSet?.currentSet ?? data?.cardSet?.previousSet;
  const branding = set?.branding;
  const { cardImageUrls } = modeConfig;
  const brandedBanner = isAffiliate
    ? {
        mobile: branding?.inviteAffiliateBackgroundMobile,
        desktop: branding?.inviteAffiliateBackgroundDesktop,
      }
    : {
        mobile: branding?.inviteBackgroundMobile,
        desktop: branding?.inviteBackgroundDesktop,
      };
  const bannerImageUrls =
    (isAffiliate ? modeConfig.affiliateBanner : modeConfig.banner) ??
    brandedBanner;
  const metaImageUrl = branding?.inviteMeta;

  useEffect(() => {
    if (!inviterName) return undefined;
    return setPageMetadata(
      formatMessage(seoMessages.title, { name: inviterName, mode: modeLabel }),
      {
        description: formatMessage(seoMessages.description, {
          name: inviterName,
          mode: modeLabel,
        }),
        img: metaImageUrl,
      }
    );
  }, [formatMessage, inviterName, metaImageUrl, modeLabel, setPageMetadata]);

  if (loading) {
    return (
      <Vertical center className={root}>
        <LoadingIndicator />
      </Vertical>
    );
  }

  if (!referrer) {
    return (
      <Navigate to={generateSportPath(ANY_SPORT_HOME, { sport })} replace />
    );
  }

  const header =
    modeConfig.rewardKind === 'credit' ? (
      <NewSeasonLogo size="medium" />
    ) : (
      <Vertical center gap={1}>
        <SorareLogo size="small" />
        {set?.branding.logo ? (
          <img className={setLogo} src={set.branding.logo} alt={set.name} />
        ) : (
          <BrandL as="p" color="var(--c-white)" className="text-center">
            {set?.name}
          </BrandL>
        )}
      </Vertical>
    );

  const rewardTitle =
    modeConfig.rewardKind === 'credit' ? (
      <FormattedMessage
        defaultMessage="Get {amount} on your first cards"
        values={{ amount: creditAmount }}
      />
    ) : isAffiliate ? (
      <FormattedMessage
        defaultMessage="Get {gems} free gems with code {code}"
        values={{ gems: gemsAmount, code: slug.toUpperCase() }}
      />
    ) : (
      <FormattedMessage
        defaultMessage="Get {gems} free gems when you join"
        values={{ gems: gemsAmount }}
      />
    );

  return (
    <SetInviteLanding
      header={header}
      bannerImageUrls={bannerImageUrls}
      bannerFallbackColor={modeConfig.fallbackColor}
      cardsImageUrl={cardImageUrls ? null : branding?.inviteCards}
      cardImageUrls={cardImageUrls}
      inviterNameColor={modeConfig.accent}
      modeLabel={<FormattedMessage {...modeConfig.labelMessage} />}
      inviterName={inviterName}
      bullets={modeConfig.bullets}
      reward={{
        icon:
          modeConfig.rewardKind === 'credit' ? (
            <MarketCredit height={40} width={40} />
          ) : (
            <GemIcon size={4} />
          ),
        title: rewardTitle,
        subtitle: isAffiliate ? (
          <FormattedMessage defaultMessage="Applied automatically when you join" />
        ) : (
          <FormattedMessage defaultMessage="Applied automatically with this invite" />
        ),
      }}
      affiliateBadgeImageUrl={isAffiliate ? referrer.pictureUrl : null}
      ctaLabel={modeConfig.ctaLabel}
      ctaHref={buildJoinHref(mode, slug, sport.toLowerCase(), affiliate)}
    />
  );
};

export default SetInvitePage;
